import type Feature from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import type { Layer } from 'ol/layer';
import type BaseLayer from 'ol/layer/Base';
import LayerGroup from 'ol/layer/Group';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import RasterSource from 'ol/source/Raster';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';

import { isNonEmptyString } from '@nora-monorepo/shared';
import type { NoraWebViewMapSource } from '@nora-monorepo/ui-shared';

import { createBaseMapOlLayer } from '#nora-ui/map/map/base-map-layer-factory';
import { DrawLayerManager } from '#nora-ui/map/map/layer-managers/DrawLayerManager';
import { FileLayerManager } from '#nora-ui/map/map/layer-managers/FileLayerManager';
import { MarkerLayerManager } from '#nora-ui/map/map/layer-managers/MarkerLayerManager';
import type { NoraLayerManager } from '#nora-ui/map/map/layer-managers/NoraLayerManager';
import { NotificationLayerManager } from '#nora-ui/map/map/layer-managers/NotificationLayerManager';
import { TrackLayerManager } from '#nora-ui/map/map/layer-managers/TrackLayerManager';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import { MapUtils } from '#nora-ui/map/map/MapUtils';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { LayerConfig, LayerConfigInfo } from '#nora-ui/map/types/layer.types';
import { CompositeLayerId } from '#nora-ui/map/types/map.types';

/**
 * Raster operation that decodes Mapbox terrain-rgb tiles into colored elevation bands.
 * Elevation formula: height = -10000 + (R * 256² + G * 256 + B) * 0.1
 *
 * Used with operationType: 'image', so the input is always ImageData[].
 * The parameter type uses the OL Operation union to satisfy the RasterSource type,
 * but at runtime only ImageData[] is passed when operationType is 'image'.
 */
function floodImage(images: ImageData[] | number[][]): ImageData | number[] {
  if (images.length === 0) {
    return [];
  }
  const firstInput = images[0];

  if (!(firstInput instanceof ImageData)) {
    return firstInput;
  }

  const imageData = firstInput;

  const rawPixels = imageData.data;
  const alpha = 196;
  for (let pixelIndex = 0; pixelIndex < rawPixels.length; pixelIndex += 4) {
    if (rawPixels[pixelIndex + 3]) {
      const height = -10000 + ((rawPixels[pixelIndex + 0] * 256 * 256 + rawPixels[pixelIndex + 1] * 256 + rawPixels[pixelIndex + 2]) * 0.1);

      if (height > 1500) {
        rawPixels[pixelIndex + 0] = 255;
        rawPixels[pixelIndex + 1] = 255;
        rawPixels[pixelIndex + 2] = 255;
        rawPixels[pixelIndex + 3] = alpha;
      }
      else if (height > 1000) {
        rawPixels[pixelIndex + 0] = 255;
        rawPixels[pixelIndex + 1] = 175;
        rawPixels[pixelIndex + 2] = 186;
        rawPixels[pixelIndex + 3] = alpha;
      }
      else if (height > 750) {
        rawPixels[pixelIndex + 0] = 175;
        rawPixels[pixelIndex + 1] = 255;
        rawPixels[pixelIndex + 2] = 186;
        rawPixels[pixelIndex + 3] = alpha;
      }
      else {
        rawPixels[pixelIndex + 3] = 0;
      }
    }
  }

  return imageData;
}

/**
 * Manages different layers on the map, including file layers, marker layers, track layers, draw layers, and notification layers.
 */
export class LayerManager {
  /**
   * Singleton instance of MapSetup for accessing map configuration and layers.
   */
  private mapSetup: MapSetup = MapSetup.getInstance();

  /**
   * Layer managers for different types of layers.
   */
  private fileLayerManager: FileLayerManager;
  private markerLayerManager: MarkerLayerManager;
  private trackLayerManager: TrackLayerManager;
  private drawLayerManager: DrawLayerManager;
  private notificationLayerManager: NotificationLayerManager;

  get file(): FileLayerManager {
    return this.fileLayerManager;
  }

  get marker(): MarkerLayerManager {
    return this.markerLayerManager;
  }

  get track(): TrackLayerManager {
    return this.trackLayerManager;
  }

  get draw(): DrawLayerManager {
    return this.drawLayerManager;
  }

  get notification(): NotificationLayerManager {
    return this.notificationLayerManager;
  }

  /**
   * Creates an instance of LayerManager.
   * @param map
   */
  constructor(private readonly map: NoraMap) {
    this.fileLayerManager = new FileLayerManager(this.map);
    this.markerLayerManager = new MarkerLayerManager(this.map);
    this.trackLayerManager = new TrackLayerManager(this.map);
    this.drawLayerManager = new DrawLayerManager(this.map);
    this.notificationLayerManager = new NotificationLayerManager(this.map);
  }

  /**
   * Sets up all layers managed by this LayerManager.
   */
  async setup(): Promise<void> {
    this.recalculateLayers();

    await this.fileLayerManager.setup();
    await this.markerLayerManager.setup();
    await this.trackLayerManager.setup();
    await this.drawLayerManager.setup();
    await this.notificationLayerManager.setup();
  }

  /**
   * Enables or disables cluster mode for all clusterable layers.
   * @param enabled Whether to enable or disable cluster mode.
   */
  setClusterMode(enabled: boolean) {
    this.fileLayerManager.setClusterMode(enabled);
    this.markerLayerManager.setClusterMode(enabled);
    this.trackLayerManager.setClusterMode(enabled);
    this.notificationLayerManager.setClusterMode(enabled);
  }

  /**
   * Checks if all clusterable layers are currently in cluster mode.
   * @returns True if all clusterable layers are clustered, false otherwise.
   */
  isEveryLayerClustered(): boolean {
    return this.fileLayerManager.isClustered
      && this.markerLayerManager.isClustered
      && this.trackLayerManager.isClustered
      && this.notificationLayerManager.isClustered;
  }

  /**
   * Gets all OpenLayers layers that have isClickable set to true.
   * Used for hover cursor changes on clickable features.
   * @returns An array of OpenLayers layers that are clickable.
   */
  getClickableLayers(): Layer[] {
    const allManagers: NoraLayerManager[] = [
      this.fileLayerManager,
      this.markerLayerManager,
      this.trackLayerManager,
      this.drawLayerManager,
      this.notificationLayerManager,
    ];

    return allManagers
      .filter(manager => manager.isClickable)
      .map(manager => manager.getLayer());
  }

  /**
   * Adds an offline map source to the map configuration.
   * This method is called when the native layer sends an event that a new offline map is available locally.
   * Updates this map instance's configuration state and recalculates the overlay layers.
   * @param mapSource The offline map source to add.
   */
  addOfflineMap(mapSource: NoraWebViewMapSource) {
    // Check if this offline map already exists in the config
    const existingMap = this.map.ref.offlineMaps.find(map => map.id === mapSource.id);

    if (existingMap) {
      // Already exists, just ensure it's visible
      if (!existingMap.visible) {
        this.map.ref.setOfflineMapVisibility(mapSource.id, true);
      }
    }
    else {
      this.map.ref.offlineMaps = [
        ...this.map.ref.offlineMaps,
        {
          id: mapSource.id,
          visible: true,
          name: mapSource.name,
        },
      ];
    }

    this.mapSetup.addOfflineMap(mapSource);

    this.recalculateLayers();
  }

  /**
   * Helpers to get the available base maps from the map setup.
   */
  getAvailableBaseMaps(): LayerConfigInfo[] {
    return this.mapSetup.getBaseMaps();
  }

  /**
   * Gets the overlay layer group.
   * @returns The overlay layer group, or null if not found.
   */
  getOverlayLayerGroup(): LayerGroup | null {
    return this.getLayerGroupById(CompositeLayerId.NoraOverlays);
  }

  /**
   * Gets a layer group by its ID.
   * @param layerId The ID of the layer group to retrieve.
   * @returns The layer group with the specified ID, or null if not found.
   */
  getLayerGroupById(layerId: string): LayerGroup | null {
    const olMap = this.map.getOlMap();

    if (!olMap) {
      return null;
    }

    const group = olMap.getLayers().getArray().find(
      layer => layer.get('id') === layerId && layer instanceof LayerGroup,
    ) as LayerGroup | undefined;

    return group ?? null;
  }

  /**
   * Gets all OpenLayers base layers from the map.
   * @returns An array of OpenLayers base layers.
   */
  getOlBaseLayers(): BaseLayer[] {
    const olMap = this.map.getOlMap();

    if (!olMap) {
      return [];
    }

    const layers = olMap.getLayers();

    return layers.getArray() ?? [];
  }

  /**
   * Gets the attribution text for a given layer.
   * @param layer The layer for which to get the attribution.
   * @param showOnlyForVisibleLayers Whether to show attribution only for visible layers.
   * @returns The attribution text, or undefined if not available.
   */
  getAttributionForLayer(layer: Layer, showOnlyForVisibleLayers: boolean): string | undefined {
    const layerId = layer.get('id') as string | undefined;
    if (layerId === undefined) {
      return undefined;
    }
    const attribution = this.mapSetup.getAttributionForLayerId(layerId);

    if (!isNonEmptyString(attribution?.text)) {
      return undefined;
    }

    if (!showOnlyForVisibleLayers) {
      return attribution.text;
    }

    if (!layer.isVisible()) {
      return undefined;
    }

    return attribution.text;
  }

  /**
   * Removes a feature from all layers on the map.
   * @param featureOrFeatureId The feature or feature ID to remove.
   */
  removeFeatureFromMap(
    featureOrFeatureId: Feature<Geometry> | string,
  ): void {
    // This handles both VectorLayers and groups.
    // If it's a group we need to go through the groups layers too.
    const processLayer = (layer: BaseLayer): void => {
      if (layer instanceof VectorLayer) {
        const source = layer.getSource() as VectorSource | null;
        if (source instanceof VectorSource) {
          MapUtils.removeFeatureFromSource(featureOrFeatureId, source);
        }
      }
      else if (layer instanceof LayerGroup) {
        for (const childLayer of layer.getLayers().getArray()) {
          processLayer(childLayer);
        }
      }
    };

    const olMap = this.map.getOlMapOrThrow();

    for (const layer of olMap.getLayers().getArray()) {
      processLayer(layer);
    }
  }

  private createElevationLayer(config: LayerConfig): ImageLayer<RasterSource> {
    if (typeof config.accessToken !== 'string' || config.accessToken.length === 0) {
      throw new Error(`Missing accessToken for MapBoxElevationLayer. A valid Mapbox access token is required to load elevation data.`);
    }

    const accessToken: string = config.accessToken;

    const elevationSource = new XYZ({
      url: `https://api.mapbox.com/v4/mapbox.terrain-rgb/{z}/{x}/{y}.pngraw?access_token=${accessToken}`,
      crossOrigin: 'anonymous',
    });

    const rasterSource = new RasterSource({
      sources: [elevationSource],
      operation: floodImage,
      threads: 0,
      operationType: 'image',
    });

    return new ImageLayer({
      opacity: 0.6,
      source: rasterSource,
      visible: config.visible ?? true,
      properties: { id: config.id, name: config.name },
    });
  }

  private createOlLayer(config: LayerConfig): BaseLayer {
    if (config.layerType === 'MapBoxElevationLayer') {
      return this.createElevationLayer(config);
    }

    return createBaseMapOlLayer(config);
  }

  /**
   * Sets up composite layers for different layer managers.
   */
  private setupCompositeLayers() {
    this.setupCompositeLayer(this.fileLayerManager);
    this.setupCompositeLayer(this.markerLayerManager);
    this.setupCompositeLayer(this.trackLayerManager);
    this.setupCompositeLayer(this.drawLayerManager);
    this.setupCompositeLayer(this.notificationLayerManager);
  }

  /**
   * Sets up a composite layer for a given layer manager by adding its OL layer
   * to the NORA overlays layer group.
   * @param layerManager The layer manager for which to set up the composite layer.
   */
  private setupCompositeLayer(layerManager: NoraLayerManager) {
    const overlayGroup = this.getOverlayLayerGroup();

    if (!overlayGroup) {
      return;
    }

    const olLayer = layerManager.getLayer();
    olLayer.set('id', layerManager.LAYER_ID);
    olLayer.set('name', layerManager.LAYER_NAME);

    overlayGroup.getLayers().push(olLayer);
  }

  /**
   * Recalculates the layers on the map based on the current configuration.
   * Each group (BaseMaps, OverlayMaps) is built independently, respecting the
   * order from the map's ref state — last in the array renders on top.
   */
  recalculateLayers() {
    const olMap = this.map.getOlMapOrThrow();
    const compositeLayers = this.mapSetup.getCompositeLayers();

    olMap.getLayers().clear();

    // Build empty LayerGroups for each composite layer
    const groupMap = new Map<string, LayerGroup>();

    for (const compositeConfig of compositeLayers) {
      const group = new LayerGroup({
        properties: { id: compositeConfig.id, name: compositeConfig.name },
      });
      groupMap.set(compositeConfig.id, group);
    }

    // Index config layers by ID for quick lookup
    const configById = new Map<string, LayerConfig>();

    for (const config of this.mapSetup.getBaseMaps()) {
      configById.set(config.id, config);
    }

    for (const config of this.mapSetup.getOverlayMaps()) {
      configById.set(config.id, config);
    }

    for (const config of this.mapSetup.getOfflineMaps()) {
      configById.set(config.id, config);
    }

    // Add base maps — active base map is visible, rest are hidden
    const activeBaseMapId = this.map.ref.baseMap;

    for (const baseMapConfig of this.mapSetup.getBaseMaps()) {
      const olLayer = this.createOlLayer({
        ...baseMapConfig,
        visible: baseMapConfig.id === activeBaseMapId,
      });
      groupMap.get(CompositeLayerId.BaseMaps)!.getLayers().push(olLayer);
    }

    // Add overlay maps in reverse ref order — first in the array renders on top (GIS convention)
    const overlayGroup = groupMap.get(CompositeLayerId.OverlayMaps)!;

    for (const { id, visible } of [...this.map.ref.overlayMaps].reverse()) {
      const config = configById.get(id);

      if (!config) {
        continue;
      }

      overlayGroup.getLayers().push(this.createOlLayer({ ...config, visible }));
    }

    // Add offline maps in reverse ref order
    for (const { id, visible } of [...this.map.ref.offlineMaps].reverse()) {
      const config = configById.get(id);

      if (!config) {
        continue;
      }

      overlayGroup.getLayers().push(this.createOlLayer({ ...config, visible }));
    }

    // Add all groups to the map in composite layer order
    for (const compositeConfig of compositeLayers) {
      const group = groupMap.get(compositeConfig.id)!;
      olMap.addLayer(group);
    }

    // Re-attach NORA overlay manager layers (files, markers, tracks, draw, notifications)
    this.setupCompositeLayers();
  }
}
